import {ToggleButton, ToggleButtonGroup} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {useState} from "react";
import {useMutation, useQueryClient} from "react-query";
import {PhoneNumber, Tool} from "../../api";
import {useTools, useToolConfigsForPhoneNumber, useToolConfigsForPhoneNumberQueryKey} from "../../client/queries";
import {useAddToolToPhoneNumber, useRemoveToolFromPhoneNumber, useDeletePhoneNumberToolConfig, useUpsertPhoneNumberToolConfig} from "../../client/hooks";
import DialogToolConfig from "../tooling/DialogToolConfig";
import type {ToolConfigHooks} from "../tooling/DialogToolConfig";
import SectionToolsCard from "../tooling/SectionToolsCard";
import ToolListSection from "../tooling/ToolListSection";
import {TOOL_STAGE_INBOUND, TOOL_STAGE_TWILIO, type ToolMode} from "../../config/toolStages";

interface Props {
    phoneNumber: PhoneNumber;
    canEdit: boolean;
}

export default function SectionToolsPhoneNumber({phoneNumber, canEdit}: Props) {
    return (
        <SectionToolsCard>
            <SectionToolsPhoneNumberContent phoneNumber={phoneNumber} canEdit={canEdit}/>
        </SectionToolsCard>
    );
}

function SectionToolsPhoneNumberContent({phoneNumber, canEdit}: Props) {
    const {t: translation} = useTranslation("aufgaben");
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const tools = useTools({limit: 100});
    const addTool = useAddToolToPhoneNumber();
    const removeTool = useRemoveToolFromPhoneNumber();
    const [configureTool, setConfigureTool] = useState<Tool | null>(null);
    const [mode, setMode] = useState<ToolMode>("inbound");

        const mandantIds = new Set(
        phoneNumber.usages.filter(usage => usage.type === "MANDANT").map(usage => usage.id)
    );
    const stageFilter = mode === "twilio" ? TOOL_STAGE_TWILIO : TOOL_STAGE_INBOUND;
    const visibleTools = tools.items.filter(tool => tool.stage === stageFilter);
    const unavailableToolIds = new Set(
        visibleTools
            .filter(tool => !tool.isGlobal && !tool.mandantIds.some(id => mandantIds.has(id)))
            .map(tool => tool.id)
    );
    const activeIds = new Set(phoneNumber.toolIds ?? []);

    const {mutate: toggleTool} = useMutation(
        async ({toolId, active}: {toolId: string; active: boolean}) =>
            active ? addTool(phoneNumber.id, toolId) : removeTool(phoneNumber.id, toolId),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["api", "v1", "phonenumbers", phoneNumber.id]);
            },
            onError: () => {
                enqueueSnackbar(capitalize(translation("tools-toggle-failed")), {variant: "error"});
            },
        }
    );

    return (
        <>
            <ToggleButtonGroup
                exclusive
                size="small"
                color="secondary"
                value={mode}
                onChange={(_, value) => { if (value) setMode(value as ToolMode); }}
                sx={{mb: 2}}
            >
                <ToggleButton value="inbound">{capitalize(translation("tools-mode-inbound"))}</ToggleButton>
                <ToggleButton value="twilio">{capitalize(translation("tools-mode-twilio"))}</ToggleButton>
            </ToggleButtonGroup>
            <ToolListSection
                tools={visibleTools}
                activeIds={activeIds}
                canEdit={canEdit}
                onToggle={(toolId, active) => toggleTool({toolId, active})}
                onConfigure={tool => setConfigureTool(tool)}
                configureLabel={translation("tools-configure")}
                emptyLabel={translation("tools-empty")}
                disabledIds={unavailableToolIds}
                disabledReason={translation("tools-not-available-for-phonenumber")}
            />
            {configureTool && (
                <PhoneNumberToolConfigDialog
                    phoneNumberId={phoneNumber.id}
                    tool={configureTool}
                    onClose={() => setConfigureTool(null)}
                />
            )}
        </>
    );
}

function PhoneNumberToolConfigDialog({phoneNumberId, tool, onClose}: { phoneNumberId: string; tool: Tool; onClose: () => void }) {
    const upsertToolConfig = useUpsertPhoneNumberToolConfig();
    const deleteToolConfig = useDeletePhoneNumberToolConfig();
    const hooks: ToolConfigHooks = {
        useConfigs: () => useToolConfigsForPhoneNumber(phoneNumberId, tool.id),
        upsert: (key, data) => upsertToolConfig(phoneNumberId, tool.id, key, data),
        remove: (key) => deleteToolConfig(phoneNumberId, tool.id, key),
        queryKey: useToolConfigsForPhoneNumberQueryKey(phoneNumberId, tool.id),
    };
    return (
        <DialogToolConfig
            open={true}
            onClose={onClose}
            tool={tool}
            hooks={hooks}
        />
    );
}
